import { ArrowDown, ArrowUp, ArrowUpDown, Search, UsersRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import { DataPanel } from '../../components/DataStates';
import { UserAvatar } from '../../components/UserAvatar';
import { useApiCollection } from '../../lib/useApiCollection';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import type {
  MembershipRole,
  Organization,
  UserDirectoryEntry,
} from '../../types/domain';

const pageSize = 25;

interface UserFilters {
  search: string;
  accountType: string;
  active: string;
  emailVerified: string;
  organization: string;
  membershipRole: MembershipRole | '';
  baVerificationStatus: string;
}

type UserDirectorySortField = 'date_joined' | 'full_name';
type UserDirectoryOrdering = UserDirectorySortField | `-${UserDirectorySortField}`;

const emptyFilters: UserFilters = {
  search: '',
  accountType: '',
  active: '',
  emailVerified: '',
  organization: '',
  membershipRole: '',
  baVerificationStatus: '',
};

const joinedFormatter = new Intl.DateTimeFormat('en-PK', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'Asia/Karachi',
});
const lastActiveFormatter = new Intl.DateTimeFormat('en-PK', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'Asia/Karachi',
});

function accountLabels(user: UserDirectoryEntry) {
  const labels: Array<{ label: string; value: string }> = [];
  if (user.is_platform_admin) labels.push({ label: 'Platform admin', value: 'platform' });
  if (user.is_brand_ambassador) labels.push({ label: 'Brand ambassador', value: 'brand-ambassador' });
  if (user.is_merchandiser) labels.push({ label: 'Merchandiser', value: 'merchandiser' });
  if (user.memberships.some((membership) => membership.organization_type !== 'platform')) {
    labels.push({ label: 'Organisation member', value: 'organization-member' });
  }
  if (labels.length === 0) labels.push({ label: 'Awaiting organisation access', value: 'unaffiliated' });
  return labels;
}

export function UsersPage() {
  const { accessToken, selectedWorkspace, isPlatformAdmin } = useAuth();
  const [changingUser, setChangingUser] = useState<UserDirectoryEntry | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [changeError, setChangeError] = useState<string | null>(null);
  const [filters, setFilters] = useState<UserFilters>(emptyFilters);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [ordering, setOrdering] = useState<UserDirectoryOrdering>('-date_joined');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedSearch(filters.search.trim()),
      300,
    );
    return () => window.clearTimeout(timeout);
  }, [filters.search]);

  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
    ordering,
  });
  if (debouncedSearch) params.set('search', debouncedSearch);
  if (filters.accountType) params.set('account_type', filters.accountType);
  if (filters.active) params.set('is_active', filters.active);
  if (filters.emailVerified) params.set('email_verified', filters.emailVerified);
  if (filters.organization) params.set('organization', filters.organization);
  if (filters.membershipRole) params.set('membership_role', filters.membershipRole);
  if (filters.baVerificationStatus) {
    params.set('ba_verification_status', filters.baVerificationStatus);
  }

  const users = useApiCollection<UserDirectoryEntry>(
    'user-directory',
    `/users/?${params.toString()}`,
    accessToken,
  );
  const organizations = useApiCollection<Organization>(
    'user-directory-organizations',
    '/organizations/?page_size=100&ordering=name',
    accessToken,
  );
  const rows = users.data?.results ?? [];
  const totalCount = users.data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  async function changeAccountType(event: React.FormEvent) {
    event.preventDefault();
    if (!changingUser?.account_type_change?.allowed || !accessToken || saving) return;
    setSaving(true);
    setChangeError(null);
    try {
      await apiRequest(`/users/${changingUser.id}/change-account-type/`, {
        accessToken, method: 'POST', body: JSON.stringify({
          account_type: changingUser.account_type_change.target, reason: reason.trim(),
        }),
      });
      setChangingUser(null);
      await users.refetch();
    } catch (error) {
      setChangeError(getApiErrorMessage(error));
    } finally { setSaving(false); }
  }

  function updateFilter<Key extends keyof UserFilters>(key: Key, value: UserFilters[Key]) {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  }

  function resetFilters() {
    setFilters(emptyFilters);
    setDebouncedSearch('');
    setOrdering('-date_joined');
    setPage(1);
  }

  function toggleOrdering(field: UserDirectorySortField) {
    setOrdering((current) => (
      current === field ? `-${field}` : field
    ));
    setPage(1);
  }

  function sortDirection(field: UserDirectorySortField): 'ascending' | 'descending' | 'none' {
    if (ordering === field) return 'ascending';
    if (ordering === `-${field}`) return 'descending';
    return 'none';
  }

  function sortIcon(field: UserDirectorySortField) {
    const direction = sortDirection(field);
    if (direction === 'ascending') return <ArrowUp aria-hidden="true" size={14} />;
    if (direction === 'descending') return <ArrowDown aria-hidden="true" size={14} />;
    return <ArrowUpDown aria-hidden="true" size={14} />;
  }

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">{selectedWorkspace?.name}</p>
          <h1>Users</h1>
          <p>Inspect platform accounts and safely correct signup choices before operational history exists.</p>
        </div>
        <span className="record-count"><UsersRound size={16} /> {totalCount}</span>
      </header>

      <section className="content-card attendance-filter-card user-directory-filter-card">
        <div className="card-heading">
          <div><h2>Find users</h2><p>Filter the platform directory by account, membership, and verification state.</p></div>
          <button className="text-button" onClick={resetFilters} type="button">Reset filters</button>
        </div>
        <div className="attendance-filter-grid user-directory-filter-grid">
          <label className="attendance-search-filter">
            Search
            <span>
              <Search aria-hidden="true" size={16} />
              <input
                aria-label="Name, email, phone or BA reference"
                onChange={(event) => updateFilter('search', event.target.value)}
                placeholder="Search users"
                type="search"
                value={filters.search}
              />
            </span>
          </label>
          <label>
            Account type
            <select aria-label="Account type" onChange={(event) => updateFilter('accountType', event.target.value)} value={filters.accountType}>
              <option value="">All account types</option>
              <option value="brand_ambassador">Brand ambassadors</option>
              <option value="merchandiser">Merchandisers</option>
              <option value="organization_member">Organisation members</option>
              <option value="platform_administrator">Platform administrators</option>
              <option value="unaffiliated">Awaiting organisation access</option>
            </select>
          </label>
          <label>
            Account status
            <select aria-label="Account status" onChange={(event) => updateFilter('active', event.target.value)} value={filters.active}>
              <option value="">All statuses</option>
              <option value="true">Login active</option>
              <option value="false">Login inactive</option>
            </select>
          </label>
          <label>
            Email verification
            <select aria-label="Email verification" onChange={(event) => updateFilter('emailVerified', event.target.value)} value={filters.emailVerified}>
              <option value="">All email states</option>
              <option value="true">Verified</option>
              <option value="false">Not verified</option>
            </select>
          </label>
          <label>
            Organisation
            <select aria-label="Organisation" onChange={(event) => updateFilter('organization', event.target.value)} value={filters.organization}>
              <option value="">All organisations</option>
              {(organizations.data?.results ?? []).map((organization) => (
                <option key={organization.id} value={organization.id}>{organization.name}</option>
              ))}
            </select>
          </label>
          <label>
            Membership role
            <select aria-label="Membership role" onChange={(event) => updateFilter('membershipRole', event.target.value as MembershipRole | '')} value={filters.membershipRole}>
              <option value="">All roles</option>
              <option value="platform_admin">Platform admin</option>
              <option value="org_admin">Organisation admin</option>
              <option value="project_manager">Project manager</option>
              <option value="supervisor">Supervisor</option>
              <option value="merchandiser">Merchandiser</option>
            </select>
          </label>
          <label>
            BA verification
            <select aria-label="BA verification" onChange={(event) => updateFilter('baVerificationStatus', event.target.value)} value={filters.baVerificationStatus}>
              <option value="">All BA states</option>
              <option value="draft">Draft</option>
              <option value="pending">Pending review</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
              <option value="suspended">Suspended</option>
            </select>
          </label>
        </div>
      </section>

      <DataPanel
        empty={rows.length === 0}
        error={users.isError || organizations.isError}
        loading={users.isLoading || organizations.isLoading}
      >
        <section className="content-card data-card">
          <div aria-label="Users table" className="data-table-wrap" role="region" tabIndex={0}>
            <table className={`data-table user-directory-table${isPlatformAdmin ? ' user-directory-table-with-correction' : ''}`}>
              <colgroup>
                <col className="user-directory-col-identity" />
                <col className="user-directory-col-account" />
                <col className="user-directory-col-verification" />
                <col className="user-directory-col-memberships" />
                <col className="user-directory-col-activity" />
                <col className="user-directory-col-joined" />
                {isPlatformAdmin ? <col className="user-directory-col-correction" /> : null}
              </colgroup>
              <thead>
                <tr>
                  <th aria-sort={sortDirection('full_name')}><button aria-label="Sort by user name" className="table-sort-button" onClick={() => toggleOrdering('full_name')} type="button">User {sortIcon('full_name')}</button></th>
                  <th>Account</th><th>Verification</th><th>Memberships</th><th>Last active</th>
                  <th aria-sort={sortDirection('date_joined')}><button aria-label="Sort by joined date" className="table-sort-button" onClick={() => toggleOrdering('date_joined')} type="button">Joined {sortIcon('date_joined')}</button></th>
                  {isPlatformAdmin ? <th>Account type correction</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-directory-identity">
                        <UserAvatar className="avatar" name={user.full_name} src={user.avatar} />
                        <span>
                          <strong>{user.full_name}</strong>
                          <small>{user.email}</small>
                          <small>{user.phone || 'No phone'}</small>
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="user-directory-pills">
                        {accountLabels(user).map((label) => (
                          <span className={`status-pill status-${label.value}`} key={label.value}>{label.label}</span>
                        ))}
                        <span className={`status-pill status-${user.is_active ? 'active' : 'inactive'}`}>{user.is_active ? 'Login active' : 'Login inactive'}</span>
                      </div>
                    </td>
                    <td>
                      <strong>{user.email_verified ? 'Email verified' : 'Email not verified'}</strong>
                      {user.is_brand_ambassador ? (
                        <small>{user.ba_reference} · {user.ba_verification_status?.replaceAll('_', ' ')}</small>
                      ) : user.is_merchandiser ? (
                        <small>{user.merchandiser_reference} · organisation-employed</small>
                      ) : <small>Not a Brand Ambassador</small>}
                    </td>
                    <td>
                      {user.memberships.length > 0 ? (
                        <div className="user-directory-memberships">
                          {user.memberships.map((membership) => (
                            <span key={membership.id}>
                              <strong>{membership.organization_name}</strong>
                              <small>{membership.role.replaceAll('_', ' ')} · {membership.is_active ? 'active' : 'inactive'}</small>
                            </span>
                          ))}
                        </div>
                      ) : '—'}
                    </td>
                    <td>{user.last_seen_at ? lastActiveFormatter.format(new Date(user.last_seen_at)) : 'Never recorded'}</td>
                    <td>{joinedFormatter.format(new Date(user.date_joined))}</td>
                    {isPlatformAdmin ? <td className="user-directory-correction">
                      <button className="button secondary" type="button"
                        aria-describedby={user.account_type_change?.reason ? `account-type-reason-${user.id}` : undefined}
                        disabled={!user.account_type_change?.allowed}
                        onClick={() => { setChangingUser(user); setReason(''); setChangeError(null); }}>
                        Change account type
                      </button>
                      {user.account_type_change?.reason ? <small id={`account-type-reason-${user.id}`}>{user.account_type_change.reason}</small> : null}
                    </td> : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="attendance-pagination">
            <span>Page {page} of {totalPages} · {totalCount} matching users</span>
            <div>
              <button className="button secondary" disabled={!users.data?.previous} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">Previous</button>
              <button className="button secondary" disabled={!users.data?.next} onClick={() => setPage((current) => Math.min(totalPages, current + 1))} type="button">Next</button>
            </div>
          </div>
        </section>
      </DataPanel>
      {changingUser ? <div className="confirmation-backdrop" onClick={() => { if (!saving) setChangingUser(null); }}>
        <section className="confirmation-dialog" role="dialog" aria-modal="true" aria-labelledby="change-account-type-title"
          onClick={(event) => event.stopPropagation()} onKeyDown={(event) => { if (event.key === 'Escape' && !saving) setChangingUser(null); }}>
          <h2 id="change-account-type-title">Change account type</h2>
          <p>{changingUser.full_name} · {changingUser.email}</p>
          <p>Change to <strong>{changingUser.account_type_change?.target === 'organization' ? 'Organisation account' : 'Brand Ambassador'}</strong>.</p>
          <p>The login and existing profile information are preserved. This does not grant organisation membership or permissions.</p>
          <form className="login-form" onSubmit={(event) => { void changeAccountType(event); }}>
            <label>Reason for correction<textarea autoFocus required maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} /></label>
            {changeError ? <p className="form-error" role="alert">{changeError}</p> : null}
            <div className="confirmation-actions">
              <button className="button secondary" type="button" disabled={saving} onClick={() => setChangingUser(null)}>Cancel</button>
              <button className="button primary" type="submit" disabled={saving || !reason.trim()}>{saving ? 'Saving…' : 'Confirm account type change'}</button>
            </div>
          </form>
        </section>
      </div> : null}
    </>
  );
}
